import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import apiService from './Common/Configurations/APIService';
import config from './Common/Configurations/APIConfig';
import { toast } from 'react-toastify';

const BrandingEditor = ({ initialData, onNext }) => {
    // ⭐ Main Form State (Parsed JSONs from WebsiteManager)
    const [form, setForm] = useState(initialData);
    const [logoFile, setLogoFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // ⭐ Generic Section Update
    const updateSection = (section, key, value) => {
        setForm(prev => ({
            ...prev,
            [section]: { ...prev[section], [key]: value }
        }));
    };

    // ⭐ Branding Specific Update
    const updateBranding = (key, value) => {
        setForm(prev => ({
            ...prev,
            branding: { ...prev.branding, [key]: value }
        }));
    };

    // ⭐ Logo Upload Handler
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const url = URL.createObjectURL(file);
            updateBranding('logoUrl', url);
        }
    };

    // ⭐ Dynamic Phone List Handlers
    const handlePhoneChange = (index, value) => {
        const newPhones = [...(form.branding.phones || [])];
        newPhones[index] = value;
        updateBranding('phones', newPhones);
    };
    const addPhone = () => updateBranding('phones', [...(form.branding.phones || []), ""]);
    const removePhone = (index) => updateBranding('phones', form.branding.phones.filter((_, i) => i !== index));

    // ⭐ Social Media Handler
    const updateSocial = (key, value) => {
        setForm(prev => ({
            ...prev,
            branding: { 
                ...prev.branding, 
                socials: { ...prev.branding.socials, [key]: value } 
            }
        }));
    };

    const handleSaveBranding = async () => {
        setIsSaving(true);
        try {
            // ⭐ Prepare FormData for Backend FormPost support
            const formData = new FormData();
           formData.append("CompanyId", form.companyId);
            formData.append("BranchId", form.branchId);
            formData.append("UserId", form.userId);
            formData.append("TemplateId", form.templateId);
            formData.append("TemplateType", form.templateType);
            formData.append("TemplateReference", form.templateReference); // FIXED: No longer undefined
            formData.append("Domain", form.domain);

            // ⭐ Send all JSON blocks
            formData.append("CustomBrandingJson", JSON.stringify(form.branding));
            formData.append("StyleJson", JSON.stringify(form.theme));
            formData.append("TopBarJson", JSON.stringify(form.topBar));
            formData.append("HeaderJson", JSON.stringify(form.header));
            formData.append("HeroJson", JSON.stringify(form.hero));
            formData.append("FooterJson", JSON.stringify(form.footer));
            formData.append("AboutUsJson", JSON.stringify(form.aboutUs));
            formData.append("ContactUsJson", JSON.stringify(form.contactUs));
            formData.append("SeoJson", JSON.stringify(form.seo));
            formData.append("DestinationsJson", JSON.stringify({ destinations: form.destinations }));

            if (logoFile) formData.append("LogoFile", logoFile);


            await apiService.post(config.ASSIGNTEMPLATE, formData);
            
            toast.success("Identity & Branding Published Successfully!");
            onNext(form); 
        } catch (e) {
            toast.error("Failed to save branding data.");
            console.error(e);
        } finally { setIsSaving(false); }
    };

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col lg={10}>
                    <Card className="shadow border-0 rounded-4">
                        <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                            <h4 className="fw-bold mb-0">Step 1: Website Identity & Branding</h4>
                            <span className="badge bg-soft-primary text-primary">Configuration Mode</span>
                        </Card.Header>
                        <Card.Body className="p-4">
                            
                            {/* --- 1. BRAND IDENTITY --- */}
                            <section className="mb-5">
                                <h6 className="text-primary fw-bold text-uppercase small mb-4">1. Brand Identity</h6>
                                <Row className="g-4">
                                    <Col md={12}>
                                        <div className="p-3 border rounded bg-light">
                                            <Form.Label className="small fw-bold">Company Logo</Form.Label>
                                            <div className="d-flex align-items-center gap-4 mt-2">
                                                <div className="bg-white p-2 border rounded" style={{width: '150px', height:'60px', display:'flex', alignItems:'center', justifyContent:'center'}}>
                                                    <img src={form.branding.logoUrl} alt="Logo" style={{maxHeight:'100%', maxWidth:'100%'}} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <Form.Control type="file" size="sm" accept="image/*" onChange={handleLogoUpload} />
                                                    <small className="text-muted mt-1 d-block">Recommended: Transparent PNG (200x60px)</small>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold">Company Name</Form.Label>
                                        <Form.Control value={form.branding.companyName} onChange={(e) => updateBranding('companyName', e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold">Brand Display Name</Form.Label>
                                        <Form.Control value={form.branding.brandName} onChange={(e) => updateBranding('brandName', e.target.value)} />
                                    </Col>
                                </Row>
                            </section>

                            {/* --- 2. CONTACT CHANNELS --- */}
                            <section className="mb-5">
                                <h6 className="text-primary fw-bold text-uppercase small mb-4">2. Contact Channels</h6>
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold">Support Email</Form.Label>
                                        <Form.Control type="email" value={form.branding.email} onChange={(e) => updateBranding('email', e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold">WhatsApp Number</Form.Label>
                                        <Form.Control value={form.branding.whatsapp} onChange={(e) => updateBranding('whatsapp', e.target.value)} />
                                    </Col>
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">Customer Support Phones</Form.Label>
                                        {(form.branding.phones || []).map((phone, idx) => (
                                            <div key={idx} className="d-flex gap-2 mb-2">
                                                <Form.Control value={phone} onChange={(e) => handlePhoneChange(idx, e.target.value)} placeholder={`Phone Line ${idx+1}`} />
                                                {idx > 0 && <Button variant="outline-danger" onClick={() => removePhone(idx)}>✖</Button>}
                                                {idx === (form.branding.phones?.length - 1) && <Button variant="outline-primary" onClick={addPhone}>➕</Button>}
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            </section>

                            {/* --- 3. SOCIAL MEDIA --- */}
                            <section className="mb-5">
                                <h6 className="text-primary fw-bold text-uppercase small mb-4">3. Social Media Presence</h6>
                                <Row className="g-3">
                                    {['facebook', 'instagram', 'x', 'linkedin', 'youtube'].map(s => (
                                        <Col md={6} key={s}>
                                            <Form.Label className="small fw-bold text-capitalize">{s} Profile URL</Form.Label>
                                            <Form.Control 
                                                placeholder={`https://${s}.com/yourprofile`}
                                                value={form.branding.socials?.[s] || ''} 
                                                onChange={(e) => updateSocial(s, e.target.value)} 
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </section>

                            {/* --- 4. DOMAIN INFO (NON-EDITABLE) --- */}
                            <section className="mb-5">
                                <h6 className="text-primary fw-bold text-uppercase small mb-4">4. Domain Configuration</h6>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">Active Website Domain</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-secondary text-muted fw-bold">https://</span>
                                            <Form.Control 
                                                className="bg-light text-muted" 
                                                value={form.domain} 
                                                disabled 
                                                style={{cursor: 'not-allowed'}}
                                            />
                                            <span className="input-group-text bg-white border-start-0">
                                                <i className="ri-lock-line text-muted"></i>
                                            </span>
                                        </div>
                                        <small className="text-muted d-block mt-2">Domains are locked for security. Please contact support to change your domain.</small>
                                    </Col>
                                </Row>
                            </section>

                            {/* --- 5. SEO SETTINGS --- */}
                            <section className="mb-4">
                                <h6 className="text-primary fw-bold text-uppercase small mb-4">5. SEO (Search Engine Optimization)</h6>
                                <div className="p-3 mb-4 border rounded bg-light">
                                    <small className="text-muted fw-bold d-block mb-2">Google Search Preview:</small>
                                    <div style={{color: '#1a0dab', fontSize: '18px', textDecoration: 'underline'}}>{form.seo.metaTitle || "Your Website Title"}</div>
                                    <div style={{color: '#006621', fontSize: '14px'}}>https://{form.domain || "yourdomain.com"}</div>
                                    <div style={{color: '#545454', fontSize: '13px'}}>{form.seo.metaDescription || "Search results summary will appear here."}</div>
                                </div>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">Meta Title</Form.Label>
                                        <Form.Control value={form.seo.metaTitle} onChange={(e) => updateSection('seo', 'metaTitle', e.target.value)} />
                                    </Col>
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">Meta Description</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={form.seo.metaDescription} onChange={(e) => updateSection('seo', 'metaDescription', e.target.value)} />
                                    </Col>
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">Keywords (Comma separated)</Form.Label>
                                        <Form.Control value={form.seo.metaKeywords} onChange={(e) => updateSection('seo', 'metaKeywords', e.target.value)} />
                                    </Col>
                                </Row>
                            </section>

                            <div className="d-flex justify-content-end gap-3 border-top pt-4">
                                <Button variant="light" className="px-4 fw-bold" onClick={() => window.close()}>Cancel</Button>
                                <Button variant="primary" className="px-5 fw-bold shadow" onClick={handleSaveBranding} disabled={isSaving}>
                                    {isSaving ? 'Processing...' : 'SAVE & CONTINUE TO VISUALS ✦'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default BrandingEditor;